import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import { useEffect, useMemo, useState } from "react";

import { AdminViewApi } from "../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { LabeledSelect } from "../../components/labeledSelect/LabeledSelect";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import { exportTableData } from "./utils/exportTableData";
import styles from "./AdminViewSection.module.scss";

const getProductId = (entry: ILicenseLogEntry): string => {
  try {
    const e = entry as any;
    // direct fields
    if (e.PRODUCT_ID) return String(e.PRODUCT_ID);
    if (e.product_id) return String(e.product_id);
    if (e.ProductId) return String(e.ProductId);
    if (e.PRODUCTID) return String(e.PRODUCTID);
    if (e["Product ID"]) return String(e["Product ID"]);

    // nested product object
    if (e.PRODUCT && typeof e.PRODUCT === "object" && e.PRODUCT.PRODUCT_ID)
      return String(e.PRODUCT.PRODUCT_ID);
    if (e.PRODUCT && Array.isArray(e.PRODUCT) && e.PRODUCT[0]?.PRODUCT_ID)
      return String(e.PRODUCT[0].PRODUCT_ID);

    // nested PRODUCTS array (from contracts mock)
    if (e.PRODUCTS && Array.isArray(e.PRODUCTS) && e.PRODUCTS[0]?.PRODUCT_ID)
      return String(e.PRODUCTS[0].PRODUCT_ID);

    // mapped field names
    if (e.MAPPED_PRODUCT_ID) return String(e.MAPPED_PRODUCT_ID);
    if (e.product) return String(e.product);

    // fallback: if entry is a plain string
    if (typeof entry === "string") return entry;
  } catch (err) {
    // ignore and return empty
  }

  return "";
};

const extractCandidatesFromEntry = (entry: ILicenseLogEntry): string[] => {
  const result: string[] = [];
  try {
    const e = entry as any;
    const inspect = (val: any) => {
      if (val === null || val === undefined) return;
      if (typeof val === "string") {
        result.push(val);
        return;
      }
      if (typeof val === "number") {
        result.push(String(val));
        return;
      }
      if (Array.isArray(val)) {
        for (const it of val) inspect(it);
        return;
      }
      if (typeof val === "object") {
        for (const k of Object.keys(val)) inspect(val[k]);
      }
    };

    inspect(e);
  } catch (err) {
    // ignore
  }

  // filter for likely product id tokens (uppercase alnum, length 2-10)
  return Array.from(
    new Set(
      result
        .map((s) => String(s).trim())
        .flatMap((s) => s.split(/[^A-Z0-9_\-]+/))
        .filter((token) => {
          // must be 2-10 chars, contain at least one letter, and be uppercase/alnum/_/-
          return /^[A-Z0-9_\-]{2,10}$/.test(token) && /[A-Z]/.test(token);
        })
    )
  );
};

export const ReleasesSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const [productOptions, setProductOptions] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [releaseData, setReleaseData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const fetchProductOptions = async () => {
    setIsLoading(true);
    await request.send(async () => {
      // Fetch all releases and derive unique PRODUCT_ID values
      const releases = await AdminViewApi.getAllReleases();
      let productIds = Array.from(
        new Set(releases.map(getProductId).filter((id) => id.length > 0))
      );

      // As a safety fallback, scan entries for uppercase tokens if none found
      if (productIds.length === 0) {
        const candidates = releases.flatMap(extractCandidatesFromEntry);
        productIds = Array.from(new Set(candidates));
      }

      // filter out numeric-only tokens and ensure unique sorted list
      productIds = productIds.filter((id) => /[A-Z]/.test(id));
      productIds.sort();
      setProductOptions(productIds);
      if (productIds.length > 0 && !selectedProduct) {
        setSelectedProduct(productIds[0]);
      }
    });
    setIsLoading(false);
  };

  useEffect(() => {
    void fetchProductOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReleaseData = async (productId: string, markLoaded = false) => {
    if (!productId) {
      setReleaseData([]);
      setHasLoaded(true);
      return;
    }

    setIsLoading(true);
    await request.send(async () => {
      const data = await AdminViewApi.getReleases(productId);
      setReleaseData(data);
      setHasLoaded(true);
    });
    setIsLoading(false);
  };

  const onConfirm = async () => {
    await fetchReleaseData(selectedProduct, true);
  };

  const filteredData = useMemo(
    () => releaseData,
    [releaseData]
  );

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = filteredData) => {
    exportTableData(rows, format, `releases_${selectedProduct || "all"}`, "Releases");
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
    </Menu>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.controls}>
        <LabeledSelect
          className={styles.yearSelect}
          items={productOptions}
          label={t(texts.adminView.product)}
          onSelect={(productId) => {
            if (productId === undefined) return;
            setSelectedProduct(productId);
          }}
        //   placeholder={t(texts.adminView.selectProduct)}
          renderOptionTitle={(productId) => productId}
          selected={selectedProduct || undefined}
        />
        <Button
          className={styles.confirmButton}
          intent="primary"
          text={t(texts.adminView.confirm)}
          onClick={onConfirm}
          loading={isLoading}
          disabled={!selectedProduct}
        />
        {hasLoaded && (
          <div className={styles.countHeader}>{visibleCount} Records Found</div>
        )}
      </div>

      {hasLoaded && (
        <div className={styles.tableContainer}>
          <Table2
            data={filteredData}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noReleaseData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};
